import SectionPicker from '~/components/SectionPicker';
import CheckboxList from '~/components/CheckboxList';
import { ACCESS_FULL, ACCESS_LIMITED } from '~/lib/usePermissions';
import ErrorMessageBlock from '~/components/ErrorMessageBlock';
import { useBookableResourcesQuery } from '~/lib/Queries';

/**
 * These should correspond to the backend in UserPermissions.php
 */
const allPermissions = [
    {
        category: 'General',
        items: [
            {
                value: 'clients',
                label: 'Clients',
                limitingBookableResourcesMessage:
                    "This user will be able to see all clients, even ones booked on people/things they don't have access to.",
            },
            { value: 'forms', label: 'Forms' },
            { value: 'marketing', label: 'Marketing Automations' },
            { value: 'insights', label: 'Insights' },
            {
                value: 'reports',
                label: 'Reports',
                limitingBookableResourcesMessage:
                    "This user will be able to see reports on all events, even ones for people/things they don't have access to.",
            },
            {
                value: 'integrations',
                label: 'Features & Integrations > Calendar Syncing',
            },
        ],
    },
    {
        category: 'Scheduling',
        items: [
            { value: 'availability', label: 'Availability' },
            { value: 'eventTypes', label: 'Event Types' },
        ],
    },
    {
        category: 'Selling',
        items: [
            { value: 'packages', label: 'Packages / Subscriptions' },
            { value: 'coupons', label: 'Coupons' },
            { value: 'giftcertificates', label: 'Gift Certificates' },
            { value: 'invoice', label: 'Invoices' },
        ],
    },
];

const UserPermissions = ({ value, setValue, className }) => {
    const { data: bookableResources } = useBookableResourcesQuery();
    const accessLevel = value?.role || ACCESS_FULL;

    function setAccessLevel(newLevel) {
        let newValue = {
            role: newLevel,
        };

        if (!value?.permissions?.length && newLevel === ACCESS_LIMITED) {
            // Check everything...
            let permissions = [];
            allPermissions.forEach(group => {
                group.items.forEach(p => permissions.push(p.value));
            });
            newValue['permissions'] = permissions;
        }

        setValue({
            ...value,
            ...newValue,
        });
    }

    function hasPermission(permission) {
        const permissions = value?.permissions || [];
        return permissions.indexOf(permission) > -1;
    }

    function setPermission(permission, enabled) {
        let permissions = value?.permissions || [];
        if (enabled && permissions.indexOf(permission) < 0) {
            // We want to enable it and it's not there
            permissions = [...permissions, permission];
        }

        if (!enabled && permissions.indexOf(permission) > -1) {
            // We want to disable it
            permissions = [...permissions];
            permissions.splice(permissions.indexOf(permission), 1);
        }

        setValue({
            ...value,
            permissions: permissions,
        });
    }

    function hasBookableResourceAccess(resourceId) {
        const resourceAccess = value?.bookableResources || [];
        return resourceAccess.indexOf(resourceId) > -1;
    }

    function setBookableResourceAccess(resourceId, enabled) {
        let resourceAccess = value?.bookableResources || [];

        if (enabled && resourceAccess.indexOf(resourceId) < 0) {
            // Enable access to this resource
            resourceAccess = [...resourceAccess, resourceId];
        }

        if (!enabled && resourceAccess.indexOf(resourceId) > -1) {
            // Disable access to this resource
            resourceAccess = [...resourceAccess];
            resourceAccess.splice(resourceAccess.indexOf(resourceId), 1);
        }

        setValue({
            ...value,
            bookableResources: resourceAccess,
        });
    }

    // Default to all bookable resources if none selected
    if (
        accessLevel === ACCESS_LIMITED &&
        bookableResources?.length &&
        (!value?.bookableResources || value.bookableResources.length === 0)
    ) {
        const allResourceIds = bookableResources.map(resource => resource.id);
        setValue({
            ...value,
            bookableResources: allResourceIds,
        });
    }

    const isLimitingBookableResources =
        value?.bookableResources?.length > 0 &&
        value?.bookableResources?.length < bookableResources.length;

    return (
        <div className={className}>
            <SectionPicker
                value={accessLevel}
                onChange={setAccessLevel}
                options={[
                    { value: ACCESS_FULL, label: 'Full' },
                    { value: ACCESS_LIMITED, label: 'Limited' },
                ]}
                className="mb-4"
            />

            {accessLevel === ACCESS_FULL && (
                <div>
                    This user has access to everything, except changing user
                    permissions and deleting this business.
                </div>
            )}

            {accessLevel === ACCESS_LIMITED && (
                <div>
                    {!value?.permissions?.length && (
                        <ErrorMessageBlock className="mb-4">
                            This user cannot access anything, please select some
                            permissions below
                        </ErrorMessageBlock>
                    )}
                    {allPermissions.map(group => (
                        <div className="mb-4" key={group.category}>
                            <div className="panel-label">{group.category}</div>
                            <div className="panel pl-4">
                                {group.items.map((item, idx) => (
                                    <label
                                        className="flex items-start py-2"
                                        key={item.value}
                                    >
                                        <input
                                            type="checkbox"
                                            className="mr-2 mt-1"
                                            checked={hasPermission(item.value)}
                                            onChange={e =>
                                                setPermission(
                                                    item.value,
                                                    e.target.checked,
                                                )
                                            }
                                        />
                                        <div>
                                            {item.label}
                                            {isLimitingBookableResources &&
                                                !!item.limitingBookableResourcesMessage &&
                                                hasPermission(item.value) && (
                                                    <div className="text-sm text-gray-500">
                                                        {
                                                            item.limitingBookableResourcesMessage
                                                        }
                                                    </div>
                                                )}
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>
                    ))}

                    {bookableResources?.length > 1 && (
                        <div className="mb-4">
                            <div className="panel-label">
                                Bookable People/Things
                            </div>
                            <div className="panel pl-4">
                                {bookableResources.map(resource => (
                                    <label
                                        className="flex items-center py-2"
                                        key={resource.id}
                                    >
                                        <input
                                            type="checkbox"
                                            className="mr-2"
                                            checked={hasBookableResourceAccess(
                                                resource.id,
                                            )}
                                            onChange={e =>
                                                setBookableResourceAccess(
                                                    resource.id,
                                                    e.target.checked,
                                                )
                                            }
                                        />
                                        {resource.name}
                                    </label>
                                ))}

                                {isLimitingBookableResources && (
                                    <label className="flex items-start border-t py-2">
                                        <input
                                            type="checkbox"
                                            className="mr-2 mt-1"
                                            checked={hasPermission(
                                                'pastAppointments',
                                            )}
                                            onChange={e =>
                                                setPermission(
                                                    'pastAppointments',
                                                    e.target.checked,
                                                )
                                            }
                                        />
                                        <div>
                                            Past Appointment Details
                                            <div className="text-sm text-gray-500">
                                                This user will be able to view
                                                and edit the details of past
                                                appointments for clients they
                                                have access to, even ones booked
                                                on people/things they don&apos;t
                                                have access to.
                                            </div>
                                        </div>
                                    </label>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default UserPermissions;
